import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { User, UserContext } from '../types';
import { supabase, getUserContext, completeSignup, setupProject } from '../services/supabase';
import { Session } from '@supabase/supabase-js';

// Timeout for loading states (30 seconds - increased for slow Edge Function calls)
const AUTH_LOADING_TIMEOUT_MS = 30000;
// Timeout for user context fetch specifically (15 seconds)
const USER_CONTEXT_TIMEOUT_MS = 15000;
// Cache key for user context in localStorage
const USER_CONTEXT_CACHE_KEY = 'complydeck-user-context';
// Cache expiry time (5 minutes) - we'll refresh in background but show cached data immediately
const USER_CONTEXT_CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Get cached user context from localStorage.
 * Returns null if cache is missing, expired, or for a different user.
 */
function getCachedUserContext(userId: string): UserContext | null {
  try {
    const cached = localStorage.getItem(USER_CONTEXT_CACHE_KEY);
    if (!cached) return null;

    const { data, userId: cachedUserId, timestamp } = JSON.parse(cached);

    // Check if cache is for the same user
    if (cachedUserId !== userId) return null;

    // Check if cache is expired
    if (Date.now() - timestamp > USER_CONTEXT_CACHE_EXPIRY_MS) return null;

    return data as UserContext;
  } catch {
    return null;
  }
}

/**
 * Save user context to localStorage cache.
 */
function setCachedUserContext(userId: string, data: UserContext): void {
  try {
    localStorage.setItem(USER_CONTEXT_CACHE_KEY, JSON.stringify({
      data,
      userId,
      timestamp: Date.now(),
    }));
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Clear cached user context.
 */
function clearCachedUserContext(): void {
  try {
    localStorage.removeItem(USER_CONTEXT_CACHE_KEY);
  } catch {
    // Ignore localStorage errors
  }
}

export interface AuthContextType {
  user: User | null;
  userContext: UserContext | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** True once userContext has been loaded (from cache or network) for the current session */
  userContextLoaded: boolean;
  needsProjectSetup: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ success: boolean; needsEmailConfirmation?: boolean; needsSetup?: boolean; error?: string }>;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  completeUserSignup: (fullName: string) => Promise<{ success: boolean; needsSetup: boolean; error?: string }>;
  setupUserProject: (data: { company_name: string; industry?: string; description?: string }) => Promise<{ success: boolean; error?: string }>;
  refreshUserContext: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Clear all authentication-related data from browser storage.
 * This ensures a clean slate for the login flow.
 */
function clearAuthData(): void {
  try {
    // Clear localStorage items related to Supabase auth
    // Matches: sb-* (default Supabase pattern), complydeck-auth (legacy custom key), supabase-*, auth-*
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('sb-') ||
        key === 'complydeck-auth' ||  // Legacy custom key
        key.includes('supabase')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Clear sessionStorage as well (though Supabase uses localStorage by default)
    const sessionKeysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (
        key.startsWith('sb-') ||
        key === 'complydeck-auth' ||
        key.includes('supabase')
      )) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));

    // Clear all cookies related to Supabase
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.split('=');
      const trimmedName = name.trim();
      if (trimmedName.startsWith('sb-') || trimmedName.includes('supabase')) {
        document.cookie = `${trimmedName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    });

    // Clear cached user context
    clearCachedUserContext();

    console.log('[Auth] Cleared all auth data from storage');
  } catch (error) {
    console.error('[Auth] Error clearing auth data:', error);
  }
}

/**
 * Wrapper for async operations with timeout
 */
async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]);
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userContext, setUserContext] = useState<UserContext | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userContextLoaded, setUserContextLoaded] = useState(false);
  const [needsProjectSetup, setNeedsProjectSetup] = useState(false);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialAuthCompleteRef = useRef(false);
  const currentSessionIdRef = useRef<string | null>(null);

  // Clear loading timeout
  const clearLoadingTimeout = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  // Start loading timeout - if loading takes too long, just stop loading (don't logout)
  const startLoadingTimeout = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    loadingTimeoutRef.current = setTimeout(() => {
      console.warn('[Auth] Loading timeout exceeded, stopping loading state');
      // Don't force logout - just stop the loading state
      // The user will remain logged in with whatever session data we have
      setIsLoading(false);
    }, AUTH_LOADING_TIMEOUT_MS);
  }, []);

  // Handle auth failures gracefully - keep user logged in with session data
  const handleAuthFailure = useCallback((error: unknown, sessionData: Session | null) => {
    console.error('[Auth] Auth failure (keeping session):', error);

    // On error, set minimal user from session to avoid blank screen
    // Don't logout - the session is still valid even if getUserContext() failed
    if (sessionData?.user) {
      setUser({
        id: sessionData.user.id,
        email: sessionData.user.email || '',
      });
      // Don't force needsProjectSetup=true, let the user try to access pages
      // The pages will handle any missing context gracefully
    }
    setIsLoading(false);
    clearLoadingTimeout();
  }, [clearLoadingTimeout]);

  const refreshUserContext = useCallback(async () => {
    if (!session) return;
    try {
      const ctx = await withTimeout(
        getUserContext(),
        AUTH_LOADING_TIMEOUT_MS,
        'User context fetch timed out'
      );
      // Cache the user context for faster subsequent loads
      setCachedUserContext(session.user.id, ctx);
      setUserContext(ctx);
      setNeedsProjectSetup(ctx.needs_project_setup);
      if (ctx.profile && ctx.project && ctx.membership) {
        setUser({
          id: ctx.user.id,
          email: ctx.user.email,
          fullName: ctx.profile.full_name,
          project: ctx.project.domain,
          projectId: ctx.project.id,
          role: ctx.membership.role as 'admin' | 'member',
        });
      }
    } catch (error) {
      console.error('Error fetching user context:', error);
    }
  }, [session]);

  useEffect(() => {
    // Flag to prevent double-processing
    let isInitializing = true;

    // Start timeout for initial load
    startLoadingTimeout();

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      currentSessionIdRef.current = session?.user?.id || null;
      setSession(session);
      if (session) {
        // Check for cached user context first for instant loading
        const cachedCtx = getCachedUserContext(session.user.id);
        if (cachedCtx) {
          // Use cached data immediately
          setUserContext(cachedCtx);
          setUserContextLoaded(true);
          setNeedsProjectSetup(cachedCtx.needs_project_setup);
          if (cachedCtx.profile && cachedCtx.project && cachedCtx.membership) {
            setUser({
              id: cachedCtx.user.id,
              email: cachedCtx.user.email,
              fullName: cachedCtx.profile.full_name,
              project: cachedCtx.project.domain,
              projectId: cachedCtx.project.id,
              role: cachedCtx.membership.role as 'admin' | 'member',
            });
          } else if (cachedCtx.user) {
            setUser({
              id: cachedCtx.user.id,
              email: cachedCtx.user.email,
            });
          }
          setIsLoading(false);
          clearLoadingTimeout();
          initialAuthCompleteRef.current = true;
          isInitializing = false;

          // Refresh context in background to keep it up to date
          getUserContext().then(ctx => {
            setCachedUserContext(session.user.id, ctx);
            setUserContext(ctx);
            setNeedsProjectSetup(ctx.needs_project_setup);
            if (ctx.profile && ctx.project && ctx.membership) {
              setUser({
                id: ctx.user.id,
                email: ctx.user.email,
                fullName: ctx.profile.full_name,
                project: ctx.project.domain,
                projectId: ctx.project.id,
                role: ctx.membership.role as 'admin' | 'member',
              });
            }
          }).catch(() => {
            // Ignore background refresh errors - we already have cached data
          });
          return;
        }

        try {
          // No cache - fetch user context with timeout
          const ctx = await withTimeout(
            getUserContext(),
            USER_CONTEXT_TIMEOUT_MS,
            'Initial user context fetch timed out'
          );
          // Cache the result
          setCachedUserContext(session.user.id, ctx);
          setUserContext(ctx);
          setUserContextLoaded(true);
          setNeedsProjectSetup(ctx.needs_project_setup);
          if (ctx.profile && ctx.project && ctx.membership) {
            setUser({
              id: ctx.user.id,
              email: ctx.user.email,
              fullName: ctx.profile.full_name,
              project: ctx.project.domain,
              projectId: ctx.project.id,
              role: ctx.membership.role as 'admin' | 'member',
            });
          } else if (ctx.user) {
            setUser({
              id: ctx.user.id,
              email: ctx.user.email,
            });
          }
          setIsLoading(false);
          clearLoadingTimeout();
        } catch (error) {
          console.error('Error fetching initial user context:', error);
          setUserContextLoaded(true); // Mark as loaded even on error so we don't stay stuck
          handleAuthFailure(error, session);
        }
      } else {
        // No session - mark context as loaded (there's nothing to load)
        setUserContextLoaded(true);
        setIsLoading(false);
        clearLoadingTimeout();
      }
      initialAuthCompleteRef.current = true;
      isInitializing = false;
    }).catch((error) => {
      console.error('Error getting initial session:', error);
      initialAuthCompleteRef.current = true;
      isInitializing = false;
      // Don't force logout - just stop loading and let user see login page if needed
      setIsLoading(false);
      clearLoadingTimeout();
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip processing during initialization - getSession already handles it
      if (isInitializing && event === 'INITIAL_SESSION') {
        return;
      }

      // Track if this is a new session or just a token refresh
      const isNewSession = session?.user?.id !== currentSessionIdRef.current;
      const wasLoggedIn = currentSessionIdRef.current !== null;
      currentSessionIdRef.current = session?.user?.id || null;

      setSession(session);

      if (session) {
        // Only show loading for actual sign-in events, not token refreshes
        // Events: SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, USER_UPDATED, PASSWORD_RECOVERY, INITIAL_SESSION
        const shouldShowLoading = !initialAuthCompleteRef.current ||
          (event === 'SIGNED_IN' && isNewSession);

        // Check for cached user context first (for instant loading on new tabs)
        const cachedCtx = getCachedUserContext(session.user.id);
        if (cachedCtx && !shouldShowLoading) {
          // Use cached data for token refreshes - no need to fetch
          setUserContext(cachedCtx);
          setUserContextLoaded(true);
          setNeedsProjectSetup(cachedCtx.needs_project_setup);
          if (cachedCtx.profile && cachedCtx.project && cachedCtx.membership) {
            setUser({
              id: cachedCtx.user.id,
              email: cachedCtx.user.email,
              fullName: cachedCtx.profile.full_name,
              project: cachedCtx.project.domain,
              projectId: cachedCtx.project.id,
              role: cachedCtx.membership.role as 'admin' | 'member',
            });
          }
          setIsLoading(false);
          clearLoadingTimeout();
          initialAuthCompleteRef.current = true;
          return;
        }

        if (shouldShowLoading) {
          setIsLoading(true);
          startLoadingTimeout();
        }

        try {
          let ctx = await withTimeout(
            getUserContext(),
            AUTH_LOADING_TIMEOUT_MS,
            'User context fetch timed out on auth change'
          );

          // If user doesn't have a profile, create one (happens after email confirmation)
          if (ctx.needs_profile && session.user?.user_metadata?.full_name) {
            try {
              await withTimeout(
                completeSignup(session.user.user_metadata.full_name),
                AUTH_LOADING_TIMEOUT_MS,
                'Complete signup timed out'
              );
              ctx = await withTimeout(
                getUserContext(),
                AUTH_LOADING_TIMEOUT_MS,
                'User context refresh timed out'
              ); // Refresh context after profile creation
            } catch (err) {
              console.error('Error completing profile:', err);
            }
          }

          // Cache the result
          setCachedUserContext(session.user.id, ctx);
          setUserContext(ctx);
          setUserContextLoaded(true);
          setNeedsProjectSetup(ctx.needs_project_setup);
          if (ctx.profile && ctx.project && ctx.membership) {
            setUser({
              id: ctx.user.id,
              email: ctx.user.email,
              fullName: ctx.profile.full_name,
              project: ctx.project.domain,
              projectId: ctx.project.id,
              role: ctx.membership.role as 'admin' | 'member',
            });
          } else if (ctx.user) {
            setUser({ id: ctx.user.id, email: ctx.user.email });
          }
          setIsLoading(false);
          clearLoadingTimeout();
        } catch (error) {
          console.error('Error on auth change:', error);
          setUserContextLoaded(true); // Mark as loaded even on error
          // Only treat as auth failure if it was during initial load or actual sign-in
          // For token refreshes, keep the user logged in with existing data
          if (!initialAuthCompleteRef.current || (event === 'SIGNED_IN' && isNewSession)) {
            // If we have cached data, use it instead of showing failure
            if (cachedCtx) {
              setUserContext(cachedCtx);
              setNeedsProjectSetup(cachedCtx.needs_project_setup);
              if (cachedCtx.profile && cachedCtx.project && cachedCtx.membership) {
                setUser({
                  id: cachedCtx.user.id,
                  email: cachedCtx.user.email,
                  fullName: cachedCtx.profile.full_name,
                  project: cachedCtx.project.domain,
                  projectId: cachedCtx.project.id,
                  role: cachedCtx.membership.role as 'admin' | 'member',
                });
              }
              setIsLoading(false);
              clearLoadingTimeout();
            } else {
              handleAuthFailure(error, session);
            }
          } else {
            console.warn('[Auth] Token refresh failed, keeping existing session');
            setIsLoading(false);
            clearLoadingTimeout();
          }
        }
      } else {
        // Only clear user state if we were previously logged in
        // This prevents logging out on initial load when there's no session
        if (wasLoggedIn || event === 'SIGNED_OUT') {
          setUser(null);
          setUserContext(null);
          setUserContextLoaded(false);
          setNeedsProjectSetup(false);
        }
        setIsLoading(false);
        clearLoadingTimeout();
      }

      // Mark initial auth as complete after first event is processed
      initialAuthCompleteRef.current = true;
    });

    return () => {
      subscription.unsubscribe();
      clearLoadingTimeout();
    };
  }, [startLoadingTimeout, clearLoadingTimeout, handleAuthFailure]);

  const signUp = async (email: string, password: string, fullName: string): Promise<{ success: boolean; needsEmailConfirmation?: boolean; needsSetup?: boolean; error?: string }> => {
    // Get the app URL for email redirect - uses environment-specific URL
    const configModule = await import('../config');
    const appUrl = configModule.config.appUrl;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: appUrl,
      },
    });
    if (error) return { success: false, error: error.message };
    if (!data.user) return { success: false, error: 'Signup failed' };

    // Check if email confirmation is required (no session means confirmation needed)
    if (!data.session) {
      // Email confirmation required - user needs to verify email first
      return { success: true, needsEmailConfirmation: true };
    }

    // Session available - complete the signup by creating profile
    try {
      const result = await completeSignup(fullName);
      setNeedsProjectSetup(result.needs_setup);
      await refreshUserContext();
      return { success: true, needsEmailConfirmation: false, needsSetup: result.needs_setup };
    } catch (err: any) {
      // Profile creation failed but user is created - they can complete on next login
      console.error('Error completing signup:', err);
      return { success: true, needsEmailConfirmation: false, needsSetup: true };
    }
  };

  const signIn = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: error.message };
    return { success: true };
  };

  const signOut = async () => {
    clearAuthData();
    await supabase.auth.signOut();
    setUser(null);
    setUserContext(null);
    setSession(null);
    setNeedsProjectSetup(false);
  };

  const completeUserSignup = async (fullName: string): Promise<{ success: boolean; needsSetup: boolean; error?: string }> => {
    try {
      const result = await completeSignup(fullName);
      setNeedsProjectSetup(result.needs_setup);
      await refreshUserContext();
      return { success: true, needsSetup: result.needs_setup };
    } catch (error: any) {
      return { success: false, needsSetup: false, error: error.message };
    }
  };

  const setupUserProject = async (data: { company_name: string; industry?: string; description?: string }): Promise<{ success: boolean; error?: string }> => {
    try {
      await setupProject(data);
      setNeedsProjectSetup(false);
      await refreshUserContext();
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      userContext,
      session,
      isAuthenticated: !!session && !!user?.project,
      isLoading,
      userContextLoaded,
      needsProjectSetup,
      signUp,
      signIn,
      signOut,
      completeUserSignup,
      setupUserProject,
      refreshUserContext,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

